import { useState } from "react";
import { DollarSign, Play, Loader2, TrendingDown, TrendingUp, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

export default function CashFlow() {
  const { orgId, familyId } = useTenant();
  const queryClient = useQueryClient();
  const [running, setRunning] = useState(false);

  // Fetch latest forecast
  const { data: forecast, isLoading } = useQuery({
    queryKey: ["cash-flow-forecast", orgId, familyId],
    queryFn: async () => {
      if (!orgId) return null;

      let query = supabase
        .from("cash_flow_forecasts")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .limit(1);

      if (familyId) query = query.eq("family_id", familyId);

      const { data, error } = await query;
      if (error) throw error;
      return data?.[0] || null;
    },
    enabled: !!orgId,
  });

  const runForecast = async () => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("cash-flow-forecast", {
        body: { orgId, familyId, horizonDays: 90 },
      });
      if (error) throw error;
      toast.success(`Forecast generated. ${data?.days_to_shortfall ? `Shortfall in ${data.days_to_shortfall} days!` : "No shortfall projected."}`);
      queryClient.invalidateQueries({ queryKey: ["cash-flow-forecast"] });
    } catch (err) {
      toast.error("Failed to generate forecast: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setRunning(false);
    }
  };

  const dailyBalance = (forecast?.daily_balance as Array<{ date: string; balance: number }>) || [];
  const inflows = (forecast?.projected_inflows as Array<{ date: string; amount: number; source: string }>) || [];
  const outflows = (forecast?.projected_outflows as Array<{ date: string; amount: number; source: string }>) || [];

  if (isLoading) return <div className="flex-1 p-8">Loading...</div>;

  return (
    <div className="flex-1 space-y-6 px-4 py-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Cash Flow Forecasting</h1>
          <p className="text-muted-foreground">90-day forward projection of capital calls, distributions, and cash balance</p>
        </div>
        <Button onClick={runForecast} disabled={running}>
          {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
          {running ? "Generating..." : "Generate Forecast"}
        </Button>
      </div>

      {forecast && (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Current Cash</CardTitle></CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">
                  ${(dailyBalance[0]?.balance || 0).toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Min Projected</CardTitle></CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold font-mono ${(forecast.min_projected_balance || 0) < 0 ? "text-destructive" : ""}`}>
                  ${(forecast.min_projected_balance || 0).toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Total Inflows</CardTitle></CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono text-green-600">
                  <TrendingUp className="inline h-5 w-5 mr-1" />
                  ${inflows.reduce((s, i) => s + i.amount, 0).toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Total Outflows</CardTitle></CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono text-red-600">
                  <TrendingDown className="inline h-5 w-5 mr-1" />
                  ${outflows.reduce((s, o) => s + o.amount, 0).toLocaleString()}
                </div>
              </CardContent>
            </Card>
          </div>

          {forecast.days_to_shortfall && (
            <Card className="border-destructive">
              <CardContent className="flex items-center gap-3 py-4">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <div>
                  <p className="font-medium text-destructive">Cash shortfall projected in {forecast.days_to_shortfall} days</p>
                  <p className="text-sm text-muted-foreground">Review upcoming obligations and consider raising liquidity.</p>
                </div>
              </CardContent>
            </Card>
          )}

          {dailyBalance.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Projected Cash Balance</CardTitle>
                <CardDescription>90-day forward projection</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dailyBalance.filter((_, i) => i % 3 === 0)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" })} />
                      <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value: number) => [`$${value.toLocaleString()}`, "Balance"]} />
                      <ReferenceLine y={0} stroke="red" strokeDasharray="3 3" />
                      <Line type="monotone" dataKey="balance" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Projected Inflows</CardTitle>
                <CardDescription>Distributions and income</CardDescription>
              </CardHeader>
              <CardContent>
                {inflows.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inflows.map((inf, i) => (
                        <TableRow key={i}>
                          <TableCell>{new Date(inf.date).toLocaleDateString()}</TableCell>
                          <TableCell>{inf.source}</TableCell>
                          <TableCell className="text-right font-mono text-green-600">${inf.amount.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : <p className="text-sm text-muted-foreground">No projected inflows</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Projected Outflows</CardTitle>
                <CardDescription>Capital calls and fees</CardDescription>
              </CardHeader>
              <CardContent>
                {outflows.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {outflows.map((out, i) => (
                        <TableRow key={i}>
                          <TableCell>{new Date(out.date).toLocaleDateString()}</TableCell>
                          <TableCell>{out.source}</TableCell>
                          <TableCell className="text-right font-mono text-red-600">${out.amount.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : <p className="text-sm text-muted-foreground">No projected outflows</p>}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {!forecast && !isLoading && (
        <Card>
          <CardContent className="text-center py-12">
            <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No forecast generated yet.</p>
            <p className="text-sm text-muted-foreground mt-2">Click "Generate Forecast" to create a 90-day cash flow projection.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
